import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface BucketRetainConfirmationProps {
  bucketName: string;
  isImported: boolean;
  loading: boolean;
  onDeregister: (bucketName: string) => Promise<void>;
  onDestructiveDelete: (bucketName: string) => Promise<void>;
  onCancel: () => void;
}

const BucketRetainConfirmation: React.FC<BucketRetainConfirmationProps> = ({
  bucketName,
  isImported,
  loading,
  onDeregister,
  onDestructiveDelete,
  onCancel
}) => {
  const [confirmText, setConfirmText] = useState('');

  const canDestructiveDelete = confirmText === bucketName;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="max-w-lg w-full mx-4 bg-white rounded-xl shadow-xl p-6"
        >
          <div className="flex items-center mb-5">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Delete Bucket</h2>
              <p className="text-sm text-gray-500">Choose how to remove <span className="font-medium">{bucketName}</span></p>
            </div>
          </div>

          {/* Section A — only for imported buckets */}
          {isImported && (
            <div className="border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="text-base font-semibold text-gray-900 mb-1">Remove from Portal</h3>
              <p className="text-sm text-gray-600 mb-4">
                The S3 bucket and all its files remain intact in AWS.
              </p>
              <button
                onClick={() => onDeregister(bucketName)}
                disabled={loading}
                className="inline-flex items-center justify-center w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  'Remove from Portal'
                )}
              </button>
            </div>
          )}

          {/* Section B — always rendered */}
          <div className="border border-red-200 rounded-lg p-4 mb-4">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Delete Bucket from AWS</h3>
            <p className="text-sm text-gray-600 mb-3">
              The bucket must be empty. This permanently removes the S3 bucket from AWS.
            </p>
            <div className="mb-3">
              <label htmlFor="confirm-bucket-name-retain" className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-semibold">{bucketName}</span> to confirm:
              </label>
              <input
                id="confirm-bucket-name-retain"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm"
                placeholder={bucketName}
              />
            </div>
            <button
              onClick={() => onDestructiveDelete(bucketName)}
              disabled={!canDestructiveDelete || loading}
              className="inline-flex items-center justify-center w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                'Delete from AWS'
              )}
            </button>
          </div>

          {/* Action row — Cancel always visible */}
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  , document.body);
};

export default BucketRetainConfirmation;
